import { Show, useRecordContext, useRedirect, RaRecord } from 'react-admin';
import {
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  Box,
} from '@mui/material';
import { SectionCard, Row, fmtDay } from '../../shared/Panel';
import { NameCell, StatusCell, ContactKindChip, CustomerTypes } from './cells';
import { SubcontractorRuleSection } from './SubcontractorRuleSection';
import { ContractorFuelSurcharges } from '../fuelSurcharges/ContractorFuelSurcharges';
import { SubcontractorRuleHistory } from './SubcontractorRuleImport';
import {
  SubcontractorRuleOwnershipHistory,
  SubcontractorRuleOwnershipSection,
} from './SubcontractorRuleOwnership';
import { FEATURE_RULE_OWNERSHIP } from './subcontractorRuleApi';
import { FeatureGate, useFeature } from '../featureFlags/FeatureGate';
import { ClientCommunicationRules } from '../clientCommunication/ClientCommunicationRules';
import { customerPriorityText } from './customerPriority';

type LooseRecord = RaRecord & Record<string, unknown>;

const display = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);

const Header = () => {
  const r = useRecordContext<LooseRecord>();
  if (!r) return null;
  return (
    <SectionCard title="Kontrahent">
      <NameCell record={r} />
      <Box sx={{ mt: 1 }}>
        <StatusCell record={r} />
      </Box>
      <Table size="small" sx={{ mt: 1 }}>
        <TableBody>
          <Row label="Adres">
            {[r.address, r.postalCode, r.city, r.country]
              .filter(Boolean)
              .join(', ') || '—'}
          </Row>
          <Row label="Identyfikator w TMS">
            {display(r.tmsCustomerId)}
          </Row>
          {/* Waga priorytetu klienta (07.10.2026) — na razie max dla
              wszystkich, sposób liczenia poda CS; tylko informacja. */}
          <Row label="Priorytet klienta">
            {customerPriorityText(r.customerPriority)}
          </Row>
          {/* Typ klienta z TMS (`activities`, odpowiedzi Marathonu/Euvic
              16.10.2026, pyt. 38) — dosłownie, z podpowiedzią dla Trade.
              Od wydania 26.10.2026 za przełącznikiem (S-26.10-03). */}
          <FeatureGate feature="tmsContactsAndCustomerTypes">
            <Row label="Typ klienta (TMS)">
              <CustomerTypes record={r} />
            </Row>
          </FeatureGate>
          {r.blacklisted === true && (
            <Row label="Powód blokady">{display(r.blacklistReason)}</Row>
          )}
        </TableBody>
      </Table>
    </SectionCard>
  );
};

/**
 * Kontakty z kartoteki klienta.
 *
 * Lista miesza osoby po stronie Marathona (kody na `M`) z osobami
 * u kontrahenta (kody na `C`) — rodzaj po polsku z kodu TMS (odpowiedzi
 * Marathonu/Euvic 16.10.2026, pyt. 33e; `shared/contactCodes.ts`).
 */
const Contacts = () => {
  const r = useRecordContext<LooseRecord>();
  const contacts = (r?.contacts as LooseRecord[] | undefined) ?? [];
  if (!r) return null;
  return (
    <SectionCard title={`Kontakty (${contacts.length})`}>
      {contacts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Kartoteka klienta nie podaje kontaktów dla tego kontrahenta.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Osoba</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Rodzaj</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((k) => (
              <TableRow key={String(k.id)}>
                <TableCell>{display(k.corrName)}</TableCell>
                <TableCell>{display(k.phone)}</TableCell>
                <TableCell sx={{ wordBreak: 'break-all' }}>
                  {display(k.email)}
                </TableCell>
                <TableCell>
                  <ContactKindChip kod={k.typeOfActivity} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
};

/** Ostatnie zlecenia — wejście z kartoteki w konkretny transport. */
const RecentOrders = () => {
  const r = useRecordContext<LooseRecord>();
  const redirect = useRedirect();
  const orders = (r?.recentOrders as LooseRecord[] | undefined) ?? [];
  const total = (r?._count as { userOffers?: number } | undefined)?.userOffers ?? 0;
  if (!r) return null;
  return (
    <SectionCard title={`Ostatnie zlecenia (${orders.length} z ${total})`}>
      {orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak zleceń powiązanych z tym kontrahentem.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Numer</TableCell>
              <TableCell>Załadunek</TableCell>
              <TableCell>Relacja</TableCell>
              <TableCell>Stan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((z) => (
              <TableRow
                key={String(z.id)}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => redirect('show', 'user-offers', z.id as number)}
              >
                <TableCell>{display(z.externalId)}</TableCell>
                <TableCell>{fmtDay(z.fromDate as string)}</TableCell>
                <TableCell>
                  {[z.fromCity, z.toCity].filter(Boolean).join(' → ') || '—'}
                </TableCell>
                <TableCell>{display(z.planningStatus)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
};

/**
 * Reguła podwykonawcy — z przełącznikiem `subcontractorRuleOwnership`
 * (25.10.2026, S-25.10-06) z właścicielem, datą obowiązywania i własną
 * historią; bez niego jak dotąd.
 */
const RuleSection = () =>
  useFeature(FEATURE_RULE_OWNERSHIP) ? <SubcontractorRuleOwnershipSection /> : <SubcontractorRuleSection />;

const RuleHistory = () =>
  useFeature(FEATURE_RULE_OWNERSHIP) ? <SubcontractorRuleOwnershipHistory /> : <SubcontractorRuleHistory />;

/** Reguły klienta dla CS: awizacje i aktualizacje (26.10.2026, pakiet P1). */
const CommunicationRules = () => {
  const record = useRecordContext();
  return typeof record?.id === 'number' ? <ClientCommunicationRules contractorId={record.id} /> : null;
};

export const ContractorShow = () => (
  <Show title="Kontrahent">
    <Stack spacing={2} sx={{ p: 2 }}>
      <Header />
      <RuleSection />
      {/* Korekta paliwowa (makieta back office v2, B4, 21.10.2026) — za
          przełącznikiem `fuelSurcharges`; wyłączony: sekcji nie ma. */}
      <ContractorFuelSurcharges />
      {/* Historia reguły (fala B, 21.10.2026) — za przełącznikiem. */}
      <RuleHistory />
      {/* Reguły awizacji i aktualizacji klienta (26.10.2026, pakiet P1) — za
          przełącznikami `clientAvizations` i `clientUpdates`. */}
      <CommunicationRules />
      <Contacts />
      <RecentOrders />
    </Stack>
  </Show>
);
